<script setup lang="ts">
  import Sheet from '@/components/Sheet.vue'
  import { useOpenAlert } from '@/composables/use-open-alert'
  import { useAlertStore } from '@/store/alert.store'
  import { AlertStatus, alertStatusLabels, type Alert } from '@/types/nest/alert'
  import { UiButton, useToastStore } from '@logistiek-zonder-papier/lzp-library'
  import { format } from 'date-fns'
  import { nl } from 'date-fns/locale'

  const { alert } = defineProps<{
    alert: Alert | null
  }>()

  const show = defineModel<boolean>('show', { required: true })

  const alertStore = useAlertStore()
  const toastStore = useToastStore()
  const { openAlert } = useOpenAlert()

  function formatDateTime(date: string) {
    return format(new Date(date), "d MMMM yyyy 'om' HH:mm", { locale: nl })
  }

  async function updateStatus(status: AlertStatus, message: string) {
    if (!alert) return
    await alertStore.updateAlertStatus(alert.id, status)
    toastStore.success(message)
    if (status === AlertStatus.ARCHIVED) show.value = false
  }

  async function open() {
    if (!alert) return
    show.value = false
    await openAlert(alert)
  }
</script>

<template>
  <Sheet
    :is-open="show"
    :title="alert?.title ?? ''"
    :description="alert ? formatDateTime(alert.createdAt) : ''"
    @close="show = false"
  >
    <template v-if="alert">
      <span class="status" :class="`status-${alert.status}`">
        {{ alertStatusLabels[alert.status] }}
      </span>

      <p v-if="alert.description" class="description">{{ alert.description }}</p>
      <p v-else class="description is-empty">Geen omschrijving</p>
    </template>

    <template #footer>
      <div v-if="alert" class="footer">
        <div class="status-actions">
          <UiButton
            v-if="alert.status === AlertStatus.UNREAD"
            variant="link"
            size="small"
            icon="check"
            icon-position="left"
            label="Markeer als gelezen"
            @click="updateStatus(AlertStatus.READ, 'Melding gemarkeerd als gelezen')"
          />
          <UiButton
            v-if="alert.status === AlertStatus.READ"
            variant="link"
            size="small"
            icon="eyeSlash"
            icon-position="left"
            label="Markeer als ongelezen"
            @click="updateStatus(AlertStatus.UNREAD, 'Melding gemarkeerd als ongelezen')"
          />
          <UiButton
            v-if="alert.status === AlertStatus.ARCHIVED"
            variant="link"
            size="small"
            icon="refresh"
            icon-position="left"
            label="Terugzetten"
            @click="updateStatus(AlertStatus.READ, 'Melding teruggezet')"
          />
          <UiButton
            v-else
            variant="link"
            size="small"
            icon="cross"
            icon-position="left"
            label="Archiveren"
            @click="updateStatus(AlertStatus.ARCHIVED, 'Melding gearchiveerd')"
          />
        </div>

        <UiButton v-if="alert.url" variant="prominent" label="Bekijk" @click="open" />
      </div>
    </template>
  </Sheet>
</template>

<style lang="css" scoped>
  .status {
    display: inline-flex;
    align-items: center;
    gap: var(--spacing-xs);
    color: var(--colours-grey-4);
    font-size: 0.875rem;
  }

  .status::before {
    content: '';
    width: 0.5rem;
    height: 0.5rem;
    border-radius: 50%;
    border: 1px solid var(--colours-grey-4);
  }

  .status-unread {
    color: var(--colours-grey-5);
    font-weight: 600;
  }

  .status-unread::before {
    border-color: var(--colours-brand-colour-1);
    background-color: var(--colours-brand-colour-1);
  }

  .status-read::before {
    background-color: var(--colours-grey-4);
  }

  .description {
    margin: var(--spacing-s) 0 0;
    white-space: pre-line;
    overflow-wrap: anywhere;
  }

  .description.is-empty {
    color: var(--colours-grey-4);
  }

  .footer {
    flex: 1;
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: var(--spacing-s);
  }

  .status-actions {
    display: flex;
    gap: var(--spacing-xs);
  }

  .status-actions :deep(svg) {
    width: 20px;
    height: 20px;
  }
</style>
